import { useCallback } from 'react';
import { toast } from 'sonner';
import { useTaskStore } from '@/stores/taskStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useUserPatternStore } from '@/stores/userPatternStore';
import { parseTaskInput } from '@/lib/nlp/nlp';
import { findFirstAvailableSlot, toBusyIntervals } from '@/lib/scheduling/findFirstSlot';
import { formatScheduledFor, nextDay, startOfLocalDay, withTime } from '@/lib/time';
import type { Task } from '@/types';

export interface CaptureResult {
  task: Task;
  scheduled: boolean;
  rolledOver: boolean;
}

function startOfToday(): Date {
  return startOfLocalDay(new Date());
}

export function useCapture(): {
  capture: (raw: string) => CaptureResult;
} {
  const addTask = useTaskStore((s) => s.addTask);

  const capture = useCallback(
    (raw: string): CaptureResult => {
      const trimmed = raw.trim();
      if (!trimmed) {
        throw new Error('Cannot capture empty input');
      }

      // Read snapshots at capture-time (Enter), not via reactive subscriptions —
      // this hook is mounted at the AppShell root, so subscribing to the whole
      // tasks/events arrays would re-render the entire shell on every mutation.
      const tasks = useTaskStore.getState().tasks;
      const events = useCalendarStore.getState().events;
      const preferredDuration = useUserPatternStore.getState().pattern.preferredTaskDuration;

      const parsed = parseTaskInput(trimmed);
      const hasSchedulingIntent = parsed.date !== null || parsed.timeMinutes !== null;

      if (!hasSchedulingIntent) {
        // No date/time → Inbox. Preserve any parsed priority/tags/duration as metadata.
        const task = addTask({
          title: parsed.title,
          priority: parsed.priority ?? 'medium',
          energyRequired: 'medium',
          durationMinutes: parsed.durationMinutes ?? preferredDuration,
          status: 'inbox',
          tags: parsed.tags,
        });
        toast.success(`Captured to Inbox: ${task.title}`);
        return { task, scheduled: false, rolledOver: false };
      }

      const duration = parsed.durationMinutes ?? preferredDuration;
      const baseDate = parsed.date ?? startOfToday();
      const now = new Date();

      let slotStart: Date | null;
      let rolledOver = false;

      if (parsed.timeMinutes !== null) {
        const candidateStart = withTime(baseDate, parsed.timeMinutes);
        // Past-today bump: only when user didn't explicitly set the date
        if (parsed.date === null && candidateStart < now) {
          slotStart = withTime(nextDay(baseDate), parsed.timeMinutes);
        } else {
          slotStart = candidateStart;
        }
      } else {
        const busyBase = toBusyIntervals(tasks, events, baseDate);
        slotStart = findFirstAvailableSlot(baseDate, duration, busyBase, now);
        if (slotStart === null) {
          const tomorrow = nextDay(baseDate);
          const busyTomorrow = toBusyIntervals(tasks, events, tomorrow);
          slotStart = findFirstAvailableSlot(tomorrow, duration, busyTomorrow, now);
          rolledOver = true;
        }
      }

      if (slotStart === null) {
        const task = addTask({
          title: parsed.title,
          priority: parsed.priority ?? 'medium',
          energyRequired: 'medium',
          durationMinutes: duration,
          status: 'inbox',
          tags: parsed.tags,
        });
        toast.info(`Captured to Inbox (no slot found): ${task.title}`);
        return { task, scheduled: false, rolledOver: false };
      }

      const slotEnd = new Date(slotStart.getTime() + duration * 60_000);
      const task = addTask({
        title: parsed.title,
        priority: parsed.priority ?? 'medium',
        energyRequired: 'medium',
        durationMinutes: duration,
        scheduledStart: slotStart,
        scheduledEnd: slotEnd,
        status: 'scheduled',
        tags: parsed.tags,
      });

      toast.success(`Scheduled ${formatScheduledFor(slotStart)}: ${task.title}`);
      return { task, scheduled: true, rolledOver };
    },
    [addTask],
  );

  return { capture };
}

export function useScheduleAction(): {
  scheduleExistingTask: (task: Task) => Date | null;
} {
  const updateTask = useTaskStore((s) => s.updateTask);

  const scheduleExistingTask = useCallback(
    (task: Task): Date | null => {
      const today = startOfToday();
      const now = new Date();
      const tasks = useTaskStore.getState().tasks;
      const events = useCalendarStore.getState().events;
      const busyToday = toBusyIntervals(tasks, events, today);
      let slotStart = findFirstAvailableSlot(today, task.durationMinutes, busyToday, now);
      if (slotStart === null) {
        const tomorrow = nextDay(today);
        const busyTomorrow = toBusyIntervals(tasks, events, tomorrow);
        slotStart = findFirstAvailableSlot(tomorrow, task.durationMinutes, busyTomorrow, now);
      }
      if (slotStart === null) return null;
      const slotEnd = new Date(slotStart.getTime() + task.durationMinutes * 60_000);
      updateTask(task.id, {
        status: 'scheduled',
        scheduledStart: slotStart,
        scheduledEnd: slotEnd,
      });
      return slotStart;
    },
    [updateTask],
  );

  return { scheduleExistingTask };
}
